import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Loader2, Ticket } from 'lucide-react';
import { useTickets, useTicketStats } from '../hooks/useTickets';
import { StatusBadge } from '../components/tickets/StatusBadge';
import { PriorityBadge } from '../components/tickets/PriorityBadge';

const PAGE_SIZE = 20;

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function TicketList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useTickets({
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    search: search || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const { data: stats } = useTicketStats();

  const tickets = data?.tickets ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const openCount = stats?.by_status?.open ?? 0;
  const inProgressCount = stats?.by_status?.in_progress ?? 0;
  const totalCount = stats?.total ?? 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-ems-text-primary">Support</h1>
          <p className="text-sm text-ems-text-muted">Submit and track support tickets</p>
        </div>
        <button
          onClick={() => navigate('/tickets/new')}
          className="inline-flex items-center gap-2 bg-ems-green text-black px-4 py-2 rounded-lg text-sm font-semibold hover:brightness-110 transition-all"
        >
          <Plus className="h-4 w-4" />
          New Ticket
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
          <p className="text-xs font-semibold text-ems-text-muted uppercase">Open</p>
          <p className="text-2xl font-bold text-blue-400 mt-1">{openCount}</p>
        </div>
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
          <p className="text-xs font-semibold text-ems-text-muted uppercase">In Progress</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">{inProgressCount}</p>
        </div>
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
          <p className="text-xs font-semibold text-ems-text-muted uppercase">Total</p>
          <p className="text-2xl font-bold text-ems-text-primary mt-1">{totalCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full bg-ems-bg-card border border-ems-border rounded-lg px-3 py-2 text-sm text-ems-text-primary focus:outline-none focus:border-ems-green"
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="waiting">Waiting</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
              Priority
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
              className="w-full bg-ems-bg-card border border-ems-border rounded-lg px-3 py-2 text-sm text-ems-text-primary focus:outline-none focus:border-ems-green"
            >
              <option value="">All</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ems-text-muted" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full bg-ems-bg-card border border-ems-border rounded-lg pl-9 pr-3 py-2 text-sm text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:border-ems-green"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-ems-text-muted" />
          <span className="ml-2 text-sm text-ems-text-muted">Loading tickets...</span>
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-ems-text-muted">
          <Ticket className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm font-medium">No tickets found</p>
          <p className="text-xs mt-1">
            {search || statusFilter || priorityFilter
              ? 'Try adjusting your filters'
              : 'Submit a new ticket to get started'}
          </p>
        </div>
      ) : (
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ems-border">
                <th className="text-left px-5 py-3 text-xs font-semibold text-ems-text-muted uppercase">#</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ems-text-muted uppercase">Subject</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ems-text-muted uppercase">Priority</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ems-text-muted uppercase">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-ems-text-muted uppercase">Created</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr
                  key={ticket.ticket_id}
                  onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                  className="border-b border-ems-border last:border-b-0 hover:bg-ems-bg-card-hover cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3 text-ems-text-muted font-mono text-xs">
                    {ticket.ticket_number}
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-ems-text-primary font-medium">{ticket.subject}</div>
                    {ticket.category_name && (
                      <div className="text-xs text-ems-text-muted mt-0.5">{ticket.category_name}</div>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <PriorityBadge priority={ticket.priority} />
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={ticket.status} />
                  </td>
                  <td className="px-5 py-3 text-ems-text-muted text-xs whitespace-nowrap">
                    {timeAgo(ticket.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-ems-border">
              <p className="text-xs text-ems-text-muted">
                Page {page} of {totalPages} ({total} ticket{total !== 1 ? 's' : ''})
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
